import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { company } from '~/company/Company';
import Modal from '~/components/Modal/Modal';
import { ModalBody, ModalHeader } from '~/components/ModalComponents';
import { deliveryAddressStore } from '~/components/ModalDeliveryAddress/DeliveryAddressStore';
import { useGlobal } from '~/hooks/useGlobal';
import { MODAL_EDIT_ADDRESS_PARAM, useModal } from '~/hooks/useModal';
import Textarea from '~/pages/Checkout/components/Textarea/Textarea';
import { modalsService } from '~/services/Modals';

import { orderStore, userStore } from '~/stores';

import Icon from '../Icon/Icon';
import { REDIRECT_QUERY_NAME } from '../ModalDeliveryAddress/constants';

import DeliveryInstructions from './DeliveryInstructions';
import MapStatic from './MapStatic/MapStatic';
import styles from './ModalEditAddressDetails.module.scss';
import SaveButton from './SaveButton';

const ModalEditAddressDetails = observer(() => {
  const { t } = useTranslation();
  const { closeModal, goBack } = useModal();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isMobile } = useGlobal();
  const editableAddressId = searchParams.get(MODAL_EDIT_ADDRESS_PARAM);
  const redirectNext = searchParams.get(REDIRECT_QUERY_NAME);
  const geocodingRequestTimeoutRef =
    useRef<Nullable<ReturnType<typeof setTimeout>>>(null);

  useEffect(() => {
    if (editableAddressId) {
      const editableAddress = userStore.addressList.find(
        (item) => item.id === editableAddressId,
      );
      if (editableAddress) {
        const point = {
          lat: editableAddress.latitude,
          lng: editableAddress.longitude,
        };

        const selectedZone = deliveryAddressStore.getSelectedPolygon(point);

        deliveryAddressStore.setMapCenter(point);
        deliveryAddressStore.setSelectedZone(selectedZone);

        deliveryAddressStore.setDeliveryAddress({
          zip: editableAddress.postcode || '',
          country: editableAddress.country_code || '',
          city: editableAddress.city || '',
          region: '',
          address1: editableAddress.street_address_1 || '',
          address2: editableAddress.street_address_2 || '',
          addressFloor: editableAddress.floor || '',
          addressBld: editableAddress.building || '',
          addressApt: editableAddress.apartment || '',
          shortAddress: editableAddress.street_address_1 || '',
          coordinates: point,
          placeId: editableAddress.place_id || '',
          addressId: editableAddress.id,
          comment: editableAddress.comment || '',
          instructions: editableAddress.instructions || [],
          type:
            editableAddress.type ||
            (company.canDefaultAddressBeEmpty ? '' : 'home'),
        });
        deliveryAddressStore.setEditAddressData({
          addressId: editableAddress.id,
          address2: editableAddress.street_address_2 || '',
          addressBld: editableAddress.building || '',
          addressFloor: editableAddress.floor || '',
          addressApt: editableAddress.apartment || '',
          type:
            editableAddress.type ||
            (company.canDefaultAddressBeEmpty ? '' : 'home'),
          comment: editableAddress.comment || '',
          instructions: editableAddress.instructions || [],
        });

        deliveryAddressStore.setInputAddressValue(
          `${editableAddress.street_address_1}, ${editableAddress.city}`,
        );

        const cleanTimeout = () => {
          if (geocodingRequestTimeoutRef.current) {
            clearTimeout(geocodingRequestTimeoutRef.current);
            geocodingRequestTimeoutRef.current = null;
          }
        };
        if (selectedZone) {
          cleanTimeout();
          geocodingRequestTimeoutRef.current = setTimeout(() => {
            deliveryAddressStore.geocoding(false, {
              lng: point.lng,
              lat: point.lat,
            });
            geocodingRequestTimeoutRef.current = null;
          }, 100);
        } else {
          orderStore.requestETA();
        }
      }
    }
  }, [editableAddressId, userStore.addressList]);

  useEffect(() => {
    return () => {
      if (!deliveryAddressStore.deliveryAddress) {
        deliveryAddressStore.resetStore();
      }
    };
  }, []);

  const handleSaveAddress = () => {
    if (!redirectNext) {
      closeModal([MODAL_EDIT_ADDRESS_PARAM, REDIRECT_QUERY_NAME]);
      return;
    }

    navigate(redirectNext, { replace: true });
  };

  const handleCloseModal = () => {
    closeModal([MODAL_EDIT_ADDRESS_PARAM, REDIRECT_QUERY_NAME]);
  };

  const handleGoBack = () => {
    goBack([MODAL_EDIT_ADDRESS_PARAM]);
    deliveryAddressStore.setSelectedZone(null);
  };

  const handleChangeLocationName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value && value.trim().toLowerCase() === 'home') {
      deliveryAddressStore.setAddressType('home');
    } else {
      deliveryAddressStore.setAddressTypeValue(value);
    }
  };

  const handleCommentChange = (val: string) => {
    deliveryAddressStore.setDeliveryAddressComment(val);
  };

  const getFormattedType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  return (
    <Modal
      isShow
      className={styles.addressDetails}
      onBackdropDismiss={handleCloseModal}
    >
      {!isMobile && (
        <button
          className={classes(['button', styles.closeButton])}
          onClick={handleCloseModal}
        >
          <Icon type="close" size={24} />
        </button>
      )}
      <div className={styles.addressDetails__container}>
        {isMobile && (
          <ModalHeader
            isVisibleBackButton={modalsService.hasHistory}
            onBackPress={handleGoBack}
            className={styles.addressDetails__container__header}
            onClose={handleCloseModal}
            title={t('checkout:address:title')}
          />
        )}

        <ModalBody className={styles.addressDetails__container__body}>
          <div className={styles.addressDetails__container__body__wrapper}>
            <div
              className={
                styles.addressDetails__container__body__contentContainer
              }
            >
              {isMobile ? null : (
                <ModalHeader
                  onBackPress={handleCloseModal}
                  className={styles.addressDetails__container__header}
                  onClose={handleCloseModal}
                  title={t('checkout:address:titleEdit')}
                />
              )}
              <div className={styles.addressDetails__container__body__content}>
                {isMobile ? null : (
                  <div className={styles.inputContainer}>
                    <label className={styles.inputLabel}>
                      {t('checkout:locationName')}
                    </label>
                    <input
                      data-input="address-custom-name"
                      placeholder={t('addressCustomName')}
                      defaultValue={
                        deliveryAddressStore.editAddressData.type?.toLowerCase() ===
                        'home'
                          ? getFormattedType(
                              deliveryAddressStore.editAddressData.type,
                            )
                          : ''
                      }
                      value={getFormattedType(
                        deliveryAddressStore.editAddressData.type,
                      )}
                      onChange={handleChangeLocationName}
                    />
                  </div>
                )}

                <div className={styles.inputGroup}>
                  <div className={styles.inputContainer}>
                    <label className={styles.inputLabel}>
                      {t('checkout:bld')}
                      {!company.isBuildingOptional && '*'}
                    </label>
                    <input
                      data-input="address-building"
                      placeholder={t('checkout:enterBld')}
                      value={deliveryAddressStore.editAddressData.addressBld}
                      onChange={(e) =>
                        deliveryAddressStore.setDeliveryAddressBld(
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className={styles.inputContainer}>
                    <label className={styles.inputLabel}>
                      {t('checkout:floor')}
                    </label>
                    <input
                      data-input="address-floor"
                      placeholder={t('checkout:enterFloor')}
                      value={deliveryAddressStore.editAddressData.addressFloor}
                      onChange={(e) =>
                        deliveryAddressStore.setDeliveryAddressFloor(
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className={styles.inputContainer}>
                    <label className={styles.inputLabel}>
                      {t('checkout:apt')}
                    </label>
                    <input
                      data-input="address-apt"
                      placeholder={t('checkout:enterApt')}
                      value={deliveryAddressStore.editAddressData.addressApt}
                      onChange={(e) =>
                        deliveryAddressStore.setDeliveryAddressApt(
                          e.target.value,
                        )
                      }
                    />
                  </div>
                </div>

                <div className={styles.inputContainer}>
                  <label className={styles.inputLabel}>
                    {t('checkout:commentForCourierOptional')}
                  </label>
                  <Textarea
                    className={styles.textarea}
                    placeholder={t('checkout:yourComment')}
                    onChange={handleCommentChange}
                    value={deliveryAddressStore.editAddressData.comment}
                  />
                  <DeliveryInstructions classNames={styles.instructions} />
                </div>
              </div>
            </div>
            <div className={styles.saveButtonContainer}>
              <SaveButton
                onClick={handleSaveAddress}
                className={styles.proceedButton}
                title={t('checkout:address:confirm')}
                shouldValidateBuilding={company.isBuildingOptional}
              />
            </div>
          </div>

          <div className={styles.addressDetails__container__body__map}>
            {isMobile && (
              <div
                className={classes([
                  styles.inputContainer,
                  styles.mobileLocationInput,
                ])}
              >
                <label className={styles.inputLabel}>
                  {t('checkout:locationName')}
                </label>
                <input
                  data-input="address-custom-name"
                  placeholder={t('addressCustomName')}
                  value={getFormattedType(
                    deliveryAddressStore.editAddressData.type,
                  )}
                  onChange={handleChangeLocationName}
                />
              </div>
            )}
            <MapStatic />
          </div>
        </ModalBody>
      </div>
    </Modal>
  );
});

export default ModalEditAddressDetails;
